import { ApiResource } from '@/core/models/api-resource.interface';
import { StoreState } from '@/core/models/store.interface';
import { LocationQuery } from 'vue-router';
import { ActionContext, ActionTree, GetterTree, MutationTree } from 'vuex';
import { User } from './../users/user.interface';
import role, { RoleState } from './roles/role.store';
import { Team } from './team.interface';
import { TeamService } from './team.service';
import user, { UserState } from './users/user.store';

const teamService = new TeamService();

export interface TeamState {
   teamsOverview?: ApiResource<Array<Team>>;

   team?: Team;
   loadingTeam: boolean;

   user?: UserState;
   role?: RoleState;
}

const state: TeamState = {
   loadingTeam: false,
};

const getters: GetterTree<TeamState, StoreState> = {
   teamsOverview(state: TeamState) {
      return state.teamsOverview;
   },
   team(state: TeamState) {
      return state.team;
   },
};

const mutations: MutationTree<TeamState> = {
   SET_TEAMS_OVERVIEW(state: TeamState, teamsOverview?: ApiResource<Array<Team>>) {
      state.teamsOverview = teamsOverview;
   },
   REMOVE_TEAM(state: TeamState, teamId: number) {
      const teamIndex = (state.teamsOverview?.data || []).findIndex((team) => team.id === teamId);
      if (teamIndex > -1) state.teamsOverview?.data.splice(teamIndex, 1);
   },
   SET_TEAM(state: TeamState, team?: Team) {
      state.team = team;
   },
   SET_LOADING_TEAM(state: TeamState, loading: boolean) {
      state.loadingTeam = loading;
   },

   ADD_TEAM_USER(state: TeamState, user: User) {
      if (!state.team?.users?.find((u) => u.id === user.id)) {
         state.team?.users?.push(user);
      }
   },
   REMOVE_TEAM_USER(state: TeamState, userId: number) {
      const userIndex = (state.team?.users || []).findIndex((user) => user.id === userId);
      if (userIndex > -1) state.team?.users?.splice(userIndex, 1);
   },
};

const actions: ActionTree<TeamState, StoreState> = {
   async getTeams(context: ActionContext<TeamState, StoreState>, query: LocationQuery): Promise<ApiResource<Team>> {
      const teamsOverview = await teamService.getTeams(query);
      context.commit('SET_TEAMS_OVERVIEW', teamsOverview);
      return teamsOverview;
   },
   async searchTeams(context, query?: LocationQuery) {
      return teamService.getTeams(query);
   },
   async getTeam(context: ActionContext<TeamState, StoreState>, payload: { teamId: number; query: LocationQuery }): Promise<Team> {
      context.commit('SET_LOADING_TEAM', true);

      return await teamService
         .getTeam(payload.teamId, payload.query)
         .then((team) => {
            context.commit('SET_TEAM', team);
            return team;
         })
         .finally(() => context.commit('SET_LOADING_TEAM', false));
   },
   async createTeam(context: ActionContext<TeamState, StoreState>, teamBody: Team) {
      const team = await teamService.postTeam(teamBody);
      return team;
   },
   async changeTeam(context: ActionContext<TeamState, StoreState>, payload: { teamId: number; team: Team; query?: LocationQuery }) {
      const team = await teamService.putTeam(payload.teamId, payload.team, payload.query);
      context.commit('SET_TEAM', team);
      return team;
   },
   async deleteTeam(context: ActionContext<TeamState, StoreState>, teamId: number) {
      return await teamService.deleteTeam(teamId).then((response) => {
         context.commit('REMOVE_TEAM', teamId);
         return response;
      });
   },
};

export default {
   namespaced: true,
   state,
   getters,
   mutations,
   actions,
   modules: {
      user,
      role,
   },
};
